import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Lock,
  Visibility,
  VisibilityOff,
  LocationOn,
  ArrowBack,
  CheckCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch: userDispatch } = useUser();
  const [currentStep, setCurrentStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    
    // Property Details
    propertyAddress: '',
    propertyType: 'residential',
    ownershipType: 'owner',
    
    // Verification Preferences
    verificationMethod: 'digital',
    agreedToTerms: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const steps = [
    'Personal Information',
    'Property Details', 
    'Account Setup',
    'Verification Preferences'
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 0: // Personal Information
        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
        break;

      case 1: // Property Details
        if (!formData.propertyAddress.trim()) {
          newErrors.propertyAddress = 'Property address is required';
        }
        break;

      case 2: // Account Setup
        if (!formData.password) newErrors.password = 'Password is required';
        else if (formData.password.length < 8) {
          newErrors.password = 'Password must be at least 8 characters';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match';
        }
        break;

      case 3: // Verification Preferences
        if (!formData.agreedToTerms) {
          newErrors.agreedToTerms = 'You must agree to the terms and conditions';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/results');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Simulate API call for user registration
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update user context with registration data
      userDispatch({
        type: 'SET_CONTACT',
        payload: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          role: formData.ownershipType as 'owner' | 'occupier' | 'agent'
        }
      });

      userDispatch({
        type: 'SET_PROPERTY',
        payload: {
          address: {
            street: formData.propertyAddress,
            city: '',
            state: '',
            zipCode: '',
            country: 'UK'
          },
          propertyType: formData.propertyType as 'residential' | 'commercial' | 'industrial' | 'mixed'
        }
      });

      // Navigate to verification page
      navigate('/verification');
      
    } catch (error) {
      setErrors({ submit: 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={!!errors.phone}
                helperText={errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Property Address"
                multiline
                rows={3}
                value={formData.propertyAddress}
                onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
                error={!!errors.propertyAddress}
                helperText={errors.propertyAddress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Property Type</FormLabel>
                <RadioGroup
                  value={formData.propertyType}
                  onChange={(e) => handleInputChange('propertyType', e.target.value)}
                >
                  <FormControlLabel value="residential" control={<Radio />} label="Residential Property" />
                  <FormControlLabel value="hmo" control={<Radio />} label="House in Multiple Occupation (HMO)" />
                  <FormControlLabel value="commercial" control={<Radio />} label="Commercial Property" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Your Role</FormLabel>
                <RadioGroup
                  value={formData.ownershipType}
                  onChange={(e) => handleInputChange('ownershipType', e.target.value)}
                >
                  <FormControlLabel value="owner" control={<Radio />} label="Property Owner" />
                  <FormControlLabel value="landlord" control={<Radio />} label="Landlord" />
                  <FormControlLabel value="agent" control={<Radio />} label="Letting Agent" />
                  <FormControlLabel value="manager" control={<Radio />} label="Property Manager" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={!!errors.password}
                helperText={errors.password || 'Minimum 8 characters required'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Preferred Verification Method</FormLabel>
                <RadioGroup
                  value={formData.verificationMethod}
                  onChange={(e) => handleInputChange('verificationMethod', e.target.value)}
                >
                  <FormControlLabel
                    value="digital"
                    control={<Radio />}
                    label="Digital Verification - Upload documents online"
                  />
                  <FormControlLabel
                    value="visit"
                    control={<Radio />}
                    label="Property Visit - In-person verification"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Radio
                    checked={formData.agreedToTerms}
                    onChange={(e) => handleInputChange('agreedToTerms', e.target.checked)}
                  />
                }
                label="I agree to the Terms and Conditions and Privacy Policy"
              />
              {errors.agreedToTerms && (
                <Typography color="error" variant="caption" display="block">
                  {errors.agreedToTerms}
                </Typography>
              )}
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto', px: 2 }}>
          {/* Header */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="h4" gutterBottom>
                Create Your Account
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Register to continue with Level 2 assessment and verification
              </Typography>
            </CardContent>
          </Card>

          {/* Progress Stepper */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stepper activeStep={currentStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          {/* Main Form */}
          <Card>
            <CardContent sx={{ p: 4 }}>
              {loading && <LinearProgress sx={{ mb: 3 }} />}
              
              {errors.submit && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {errors.submit}
                </Alert>
              )}

              <Typography variant="h6" gutterBottom>
                {steps[currentStep]}
              </Typography>
              
              <Divider sx={{ mb: 3 }} />

              {renderStepContent()}

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                  disabled={loading}
                >
                  {currentStep === 0 ? 'Back to Results' : 'Previous'}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading}
                  startIcon={currentStep === steps.length - 1 ? <CheckCircle /> : undefined}
                >
                  {loading ? 'Creating Account...' : 
                   currentStep === steps.length - 1 ? 'Create Account' : 'Next'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </motion.div>
    </Box>
  );
};

export default SignUpPage;